
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { $t as lang } from '@/Tool'
import api from '@/services'
import vuex from '@/store'
import CertificationDialog from '@/components/CertificationDialog.vue'

export default defineComponent({
  name: 'CertificationBar',
  components: {
    CertificationDialog,
  },
  setup() {
    const router = useRouter()
    const dialogVisible = ref(false)
    const corpAuthEnabled = ref(false)
    const authorizationRequired = ref(false)
    const forceCorpAuth = ref(false)
    const successDialogVisible = ref(false)
    const barClosed = ref(false)
    const authMessageHtml = ref('') // 新增

    // 统一获取所有配置
    const getAllConfigs = async () => {
      try {
        const res = await api.ConfigServer.getDictByName('服务升级')
        if (res.success) {
          const options = res.data.childOption || []

          // 获取认证栏开关配置
          const corpAuthOption = options.find((item: any) => item.optionName === 'corpAuth')
          corpAuthEnabled.value = corpAuthOption?.optionValue == '1'

          // 获取授权书必填配置
          const authOption = options.find((item: any) => item.optionName === 'letterAuthorization')
          authorizationRequired.value = authOption?.optionValue == '1'

          // 获取强制认证配置
          const forceAuthOption = options.find((item: any) => item.optionName === 'forceCorpAuth')
          forceCorpAuth.value = forceAuthOption?.optionValue == '1'

          // 获取认证栏文案
          const authMsgOption = options.find((item) => item.optionName === 'authMessage')
          const langKey = localStorage.getItem('lang') === 'en' ? 'en' : 'zh'
          if (authMsgOption && authMsgOption.optionValue) {
            const optionVal =
              typeof authMsgOption.optionValue === 'string'
                ? JSON.parse(authMsgOption.optionValue)
                : authMsgOption.optionValue
            authMessageHtml.value = optionVal[langKey] || ''
          } else {
            authMessageHtml.value = ''
          }
        }
      } catch (error) {
        console.error('获取配置失败:', error)
        // 默认值
        corpAuthEnabled.value = false
        authorizationRequired.value = false
        forceCorpAuth.value = false
        authMessageHtml.value = ''
      }
    }

    // 根据用户认证状态和配置开关判断是否显示认证栏
    const showBar = computed(() => {
      // 如果配置开关关闭，不显示认证栏
      if (!corpAuthEnabled.value) return false

      // 如果用户关闭了认证栏，不显示
      if (barClosed.value) return false

      const userInfo = vuex.state.user.info
      // 如果用户信息不存在，显示认证栏
      if (!userInfo) return true

      // 如果 auditStatus 为 2（已认证），不显示认证栏
      if (userInfo.auditStatus === 2) return false

      // 如果 type 为 3（供应商），不显示认证栏
      if (userInfo.type === 3) return false

      // 其他情况显示认证栏
      return true
    })

    // 关闭认证栏
    const handleCloseBar = () => {
      barClosed.value = true
    }

    const handleCertification = () => {
      dialogVisible.value = true
    }

    // 重新获取企业认证信息
    const refreshEntEditionInfo = async () => {
      try {
        const response = await api.Scrm_sys_api.srcmLogin.queryEntEditionInfoByCorpId({
          data: {},
          error: false,
          success: false,
        })

        if (response.success && response.data) {
          // 更新用户信息中的企业认证状态
          const userInfo = vuex.state.user.info
          if (userInfo) {
            userInfo.auditStatus = response.data.auditStatus
            userInfo.businessLicenseUrl = response.data.businessLicenseUrl
            userInfo.authorLetterUrl = response.data.authorLetterUrl
            userInfo.corpName = response.data.name
            // 更新Vuex状态
            vuex.commit('user/setUserInfo', userInfo)
          }
        }
      } catch (error) {
        console.error('获取企业认证信息失败:', error)
      }
    }

    // 处理认证成功
    const handleCertificationSuccess = async (response: any) => {
      dialogVisible.value = false
      // 重新获取企业认证信息，更新用户状态
      await refreshEntEditionInfo()
      // 显示成功弹窗
      successDialogVisible.value = true
    }

    // 处理认证取消
    const handleCertificationCancel = () => {
      dialogVisible.value = false
    }

    const formattedMessage = computed(() => {
      const message = lang('certification.message')
      return message
        .replace(/(\d{4}年\d{1,2}月\d{1,2}日(?:\([^)]+\))?)/g, '<span class="highlight">$1</span>')
        .replace(/企业认证/g, '<span class="highlight">企业认证</span>')
    })

    // 处理成功确认
    const handleSuccessConfirm = async () => {
      successDialogVisible.value = false

      // 先获取isHuawei值，因为后面会清理sessionStorage
      const isHuawei = sessionStorage.getItem('isHuaweiyun')

      try {
        // 退出登录
        await api.Scrm_sys_api.srcmLogin.loginOut({
          error: false,
          success: false,
          data: {},
        })
      } catch (error) {
        console.error('退出登录失败:', error)
      } finally {
        // 清理本地存储和状态
        vuex.commit('clear_data')
        sessionStorage.clear()
        localStorage.removeItem('__user')

        // 跳转至登录页面
        if (isHuawei === 'huawei') {
          router.replace('/huaweiyun/login')
        } else {
          router.replace('/login')
        }
        location.reload()
      }
    }

    // 组件挂载时获取配置
    onMounted(() => {
      getAllConfigs()
    })

    return {
      dialogVisible,
      showBar,
      handleCertification,
      authMessageHtml,
      authorizationRequired,
      forceCorpAuth,
      handleCloseBar,
      successDialogVisible,
      handleSuccessConfirm,
      handleCertificationSuccess,
      handleCertificationCancel,
      lang,
    }
  },
})
